import { useCallback, type ReactNode } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import {
  buildPurchaseRequisitionRows,
  chunkPurchaseRequisitionSizes,
  getPurchaseRequisitionSizes,
  getPurchaseRequisitionStyleNo,
  isPoChangeType,
  PURCHASE_REQUISITION_TYPE,
  resolvePurchaseRequisitionImageUrl,
  type PurchaseRequisitionCard,
  type PurchaseRequisitionCardData,
  type PurchaseRequisitionPartcode,
  type PurchaseRequisitionRow,
} from '@/utils/approval/purchaseRequisition';
import { PurchaseRequisitionRoot } from './styles';

const text = (v: unknown) =>
  v === null || v === undefined ? '' : String(v).trim();

/** 레거시 number_format — 숫자가 아니면 원문 */
const formatNumber = (v: unknown) => {
  const raw = text(v);
  const n = Number(raw.replace(/,/g, ''));
  return raw && Number.isFinite(n) ? n.toLocaleString('en-US') : raw;
};

const formatQty = (n: number) => (n ? n.toLocaleString('en-US') : '0');

/** 카드 제목 칸 — 조회·작성 화면이 이 칸에 부분반려·삭제 버튼을 붙임 */
const TITLE_ATTR = 'data-pr-card-title';

/** 129 는 합의납기·생산처 변경 전후를 나란히, 바뀐 값 강조(레거시 appr_sub_view_forms_po_order_cacp_mt_mstr) */
const PoChangeRows = ({
  h,
  t,
}: {
  h: NonNullable<PurchaseRequisitionCardData['header']>;
  t: TFunction;
}) => {
  const changed = (before: unknown, after: unknown) =>
    text(before) !== text(after) ? 'pr-changed' : undefined;
  return (
    <>
      <tr>
        <td className="label">{t('성별')}</td>
        <td>{text(h.sex)}</td>
        <td className="label">{t('국가')}</td>
        <td>{text(h.poCountry)}</td>
        <td className="label">{t('요청납기')}</td>
        <td>{text(h.requestFinish)}</td>
      </tr>
      <tr>
        <td className="label">{t('대구분')}</td>
        <td>{text(h.typeName)}</td>
        <td className="label">{t('합의납기일')}</td>
        <td>{text(h.beforeMainFinishSched)}</td>
        <td className="label pr-changed-label">{t('변경 합의납기')}</td>
        <td className={changed(h.beforeMainFinishSched, h.mainFinishSched)}>
          {text(h.mainFinishSched)}
        </td>
      </tr>
      <tr>
        <td className="label">{t('복종그룹')}</td>
        <td>{text(h.clothesGroup)}</td>
        <td className="label">{t('생산처')}</td>
        <td>{text(h.beforeManufacturerName)}</td>
        <td className="label pr-changed-label">{t('변경생산처')}</td>
        <td className={changed(h.beforeManufacturerName, h.manufacturerName)}>
          {text(h.manufacturerName)}
        </td>
      </tr>
    </>
  );
};

const HeaderTable = ({
  card,
  styleNo,
  isPoChange,
  t,
}: {
  card: PurchaseRequisitionCardData;
  styleNo: string;
  isPoChange: boolean;
  t: TFunction;
}) => {
  const h = card.header ?? {};
  const imageUrl = resolvePurchaseRequisitionImageUrl(h.imageName);
  return (
    <table className="text-center">
      <colgroup>
        <col width="16%" />
        <col width="12%" />
        <col width="20%" />
        <col width="13%" />
        <col width="14%" />
        <col width="12%" />
        <col width="13%" />
      </colgroup>
      <tbody>
        <tr>
          <td rowSpan={8} className="pr-image">
            {imageUrl && <img src={imageUrl} alt={styleNo} />}
          </td>
          <th colSpan={6}>
            {t('기본정보')}-{text(h.styleName)}
          </th>
        </tr>
        <tr>
          <td rowSpan={4} className="label">
            {t('브랜드')}
          </td>
          <td rowSpan={4}>{text(h.brandName)}</td>
          <td className="label">{t('품번')}</td>
          <td colSpan={3}>{styleNo}</td>
        </tr>
        <tr>
          <td className="label">{t('운영시즌')}</td>
          <td colSpan={3}>{text(h.seasonRun)}</td>
        </tr>
        <tr>
          <td className="label">{t('패밀리품번')}</td>
          <td colSpan={3}>{text(h.familyStyleNo)}</td>
        </tr>
        <tr>
          <td className="label">Card_No</td>
          <td>{text(h.cardNo)}</td>
          <td className="label">{t('가판가')}</td>
          <td>{formatNumber(h.tempCost)}</td>
        </tr>
        {isPoChange ? (
          <PoChangeRows h={h} t={t} />
        ) : (
          <>
            <tr>
              <td className="label">{t('생산처')}</td>
              <td>{text(h.manufacturerName)}</td>
              <td className="label">{t('국가')}</td>
              <td>{text(h.poCountry)}</td>
              <td className="label">{t('요청납기')}</td>
              <td>{text(h.requestFinish)}</td>
            </tr>
            <tr>
              <td className="label">{t('대구분')}</td>
              <td>{text(h.typeName)}</td>
              <td className="label">{t('복종그룹')}</td>
              <td>{text(h.clothesGroup)}</td>
              <td className="label">{t('합의납기')}</td>
              {/* 레거시는 합의납기 값을 주석 처리해 빈 칸으로 보임 */}
              <td />
            </tr>
            <tr>
              <td className="label">{t('성별')}</td>
              <td colSpan={5}>{text(h.sex)}</td>
            </tr>
          </>
        )}
      </tbody>
    </table>
  );
};

/** 레거시: 브랜드 X 는 팀명 비움, M·I 는 '컬러코드(색상)' */
const ColorCells = ({
  row,
  brand,
}: {
  row: PurchaseRequisitionRow;
  brand: string;
}) => (
  <>
    <td>{brand === 'X' ? '' : row.teamCode}</td>
    <td>
      {brand === 'M' || brand === 'I' ? (
        <>
          <strong>{row.colorCode}</strong>({row.color})
        </>
      ) : (
        row.color
      )}
    </td>
    <td>{row.colorName}</td>
  </>
);

const sumSize = (rows: PurchaseRequisitionRow[], size: string) =>
  rows.reduce((acc, r) => acc + (r.qty[size] ?? 0), 0);

/** 사이즈가 많으면 레거시처럼 여러 표로 나눔. 행 합계는 마지막 표에, 소계는 표마다 */
const DetailTables = ({
  card,
  t,
}: {
  card: PurchaseRequisitionCardData;
  t: TFunction;
}) => {
  const brand = text(card.header?.brand).toUpperCase();
  const rows = buildPurchaseRequisitionRows(card.details);
  const groups = chunkPurchaseRequisitionSizes(
    getPurchaseRequisitionSizes(card),
  );
  const grandTotal = rows.reduce((acc, r) => acc + r.total, 0);
  const isSplit = groups.length > 1;

  return (
    <>
      {groups.map((sizes, gi) => {
        const isLast = gi === groups.length - 1;
        const columnCount = 3 + sizes.length + (isLast ? 1 : 0);
        return (
          <table key={sizes.join('|')} className="text-center pr-detail">
            <tbody>
              {gi === 0 && (
                <tr>
                  <th colSpan={columnCount}>DETAIL</th>
                </tr>
              )}
              <tr>
                <td rowSpan={2} className="label">
                  {t('팀명')}
                </td>
                <td rowSpan={2} className="label">
                  {t('색상')}
                </td>
                <td rowSpan={2} className="label">
                  {t('색상명')}
                </td>
                <td colSpan={sizes.length} className="label">
                  {t('사이즈')}
                </td>
                {isLast && (
                  <td rowSpan={2} className="label">
                    TOTAL
                  </td>
                )}
              </tr>
              <tr>
                {sizes.map((size) => (
                  <td key={size} className="label">
                    {size}
                  </td>
                ))}
              </tr>
              {rows.map((row) => (
                <tr key={`${row.teamCode}|${row.color}`}>
                  <ColorCells row={row} brand={brand} />
                  {sizes.map((size) => (
                    <td key={size}>{formatQty(row.qty[size] ?? 0)}</td>
                  ))}
                  {isLast && <td>{formatQty(row.total)}</td>}
                </tr>
              ))}
              {isSplit && (
                <tr className="pr-sum">
                  <td colSpan={3}>{t('소계')}</td>
                  {sizes.map((size) => (
                    <td key={size}>{formatQty(sumSize(rows, size))}</td>
                  ))}
                  {isLast && <td />}
                </tr>
              )}
              {isLast && (
                <tr className="pr-sum">
                  {isSplit ? (
                    <td colSpan={3 + sizes.length}>{t('합계')}</td>
                  ) : (
                    <>
                      <td colSpan={3}>{t('합계')}</td>
                      {sizes.map((size) => (
                        <td key={size}>{formatQty(sumSize(rows, size))}</td>
                      ))}
                    </>
                  )}
                  <td>{formatQty(grandTotal)}</td>
                </tr>
              )}
            </tbody>
          </table>
        );
      })}
    </>
  );
};

const CardView = ({
  card,
  styleNo,
  cardNo,
  isPoChange,
  t,
}: {
  card: PurchaseRequisitionCardData;
  styleNo: string;
  cardNo: string;
  isPoChange: boolean;
  t: TFunction;
}) => {
  const aprvId = text(card.aprvId);
  const titleAttrs = {
    [TITLE_ATTR]: '',
    'data-aprv-id': aprvId,
    'data-style-no': styleNo,
    'data-card-no': cardNo,
  };
  return (
    // 백엔드가 부분 반려된 카드를 조회 때 이 data-item-id 로 찾아 뺌
    <div className="pr-card" data-item-id={aprvId}>
      <table>
        <tbody>
          <tr>
            <th {...titleAttrs}>
              {isPoChange
                ? `Purchase Order(${styleNo} )`
                : t('Purchase Requisition({{styleNo}} , {{cardNo}}차)', {
                    styleNo,
                    cardNo,
                  })}
            </th>
          </tr>
        </tbody>
      </table>
      <HeaderTable
        card={card}
        styleNo={styleNo}
        isPoChange={isPoChange}
        t={t}
      />
      <DetailTables card={card} t={t} />
    </div>
  );
};

/** 카드 1장 — 작성 시점 HTML 로 굳혀 attributes.content 에 이어 붙임 */
export const buildPurchaseRequisitionCard = (
  card: PurchaseRequisitionCardData,
  partcodes: PurchaseRequisitionPartcode[] | null | undefined,
  t: TFunction,
  apvType: number = PURCHASE_REQUISITION_TYPE,
): PurchaseRequisitionCard => {
  const styleNo = getPurchaseRequisitionStyleNo(card, partcodes);
  const cardNo = text(card.header?.cardNo);
  return {
    aprvId: text(card.aprvId),
    styleNo,
    cardNo,
    html: renderToStaticMarkup(
      <CardView
        card={card}
        styleNo={styleNo}
        cardNo={cardNo}
        isPoChange={isPoChangeType(apvType)}
        t={t}
      />,
    ),
  };
};

export type PurchaseRequisitionCardRef = {
  aprvId: string;
  styleNo: string;
  cardNo: string;
};

/**
 * 저장 본문 그대로(레거시 문서 포함). 카드 제목 칸에 화면별 버튼을 붙임.
 * 레거시 문서는 저장 HTML 안의 삭제·부분반려 버튼 자리를 씀
 */
export const PurchaseRequisitionSnapshot = ({
  content,
  renderCardAction,
}: {
  content: string;
  renderCardAction?: (card: PurchaseRequisitionCardRef) => ReactNode;
}) => {
  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props, children }) => {
      if (tagName === 'th' && element.hasAttribute(TITLE_ATTR)) {
        const action = renderCardAction?.({
          aprvId: element.getAttribute('data-aprv-id') ?? '',
          styleNo: element.getAttribute('data-style-no') ?? '',
          cardNo: element.getAttribute('data-card-no') ?? '',
        });
        return (
          <th key={props.key as string} className="pr-card-title">
            <span className="pr-card-title-row">
              <span>{children}</span>
              {action}
            </span>
          </th>
        );
      }
      if (tagName !== 'button') return undefined;
      if (element.classList.contains('btn-detail-hide')) return null;
      if (element.classList.contains('btn-detail-reject')) {
        return (
          renderCardAction?.({
            aprvId: element.getAttribute('data-aprv_id') ?? '',
            styleNo: element.getAttribute('data-styleno') ?? '',
            cardNo: element.getAttribute('data-cardno') ?? '',
          }) ?? null
        );
      }
      return undefined;
    },
    [renderCardAction],
  );
  return (
    <PurchaseRequisitionRoot>
      <SerpContentSnapshot content={content} transform={transform} />
    </PurchaseRequisitionRoot>
  );
};

/** 부분반려 버튼을 붙일 카드 수 — V2 카드 제목 칸 + 레거시 부분반려 버튼 */
export const countPurchaseRequisitionCards = (content: string | null) =>
  content
    ? (content.match(/data-pr-card-title|btn-detail-reject/g) ?? []).length
    : 0;
